import { DateRange } from '@fullstory/client-api';
import {
  AnalysisMode,
  BadClickType,
  ComputeFunnelMetricsRequest,
  CoreWebVitalType,
  GenerateOpportunitiesRequest,
  GenerateOpportunitiesResponse,
  GenerateSignalsRequest,
  Opportunity,
  Signal,
  SignalType,
} from '@fullstory/proto/fs/services/alexa';
import { Funnel } from '@fullstory/proto/fs/services/search-settings';
import { ImpactsTableConfig } from '@fullstory/proto/fs/services/webber/internal/impacts';
import { int64, Timestamp } from '@fullstory/proto/google/protobuf';
import { BySlug, ParsedImpactsTableConfig, SpecObjectInfo } from '@fullstory/redux';
import { hashCode } from '@fullstory/util/string';
import { orderBy } from 'lodash';
import { eventsToSpecAnalytics } from '../specUtils';
import {
  ConversionAnalysisAnalytics,
  AnalysisOrigin,
  ConversionAnalysisRunAnalytics,
  GenerateOpportunityRequestAnalytics,
} from './types';
import { isNonNull } from '@fullstory/typeutils';

export const transformImpactConfigAnalytics = (
  config: ImpactsTableConfig,
): ConversionAnalysisAnalytics => {
  // Custom events cannot accept arrays of objects, so we remove/transform them here first
  const { customEvents, watchedElements, customQueries, definedEventIds, pageviewEvents, ...rest } =
    config;
  return {
    ...rest,
    customEvents: customEvents.length,
    watchedElements: watchedElements.length,
    customQueries: customQueries.length,
    definedEventIds: definedEventIds.length,
    pageviewEvents: pageviewEvents.length,
  };
};

export const transformImpactConfigAnalyticsWithoutAttributes = (
  config: ParsedImpactsTableConfig,
): ConversionAnalysisAnalytics => transformImpactConfigAnalytics(removeParsedAttributes(config));

const removeParsedAttributes = (config: ParsedImpactsTableConfig): ImpactsTableConfig => {
  const { ParsedFunnel, ParsedSegment, ...rest } = config;
  return rest;
};

export const conversionsAnalysisRun = (params: {
  signalParams: Pick<
    GenerateSignalsRequest,
    | 'badClicks'
    | 'performanceThresholds'
    | 'ajaxErrors'
    | 'customEvents'
    | 'definedEvents'
    | 'seenEvents'
    | 'coreWebVitals'
    | 'pageViews'
  >;
  funnelRequest: Omit<ComputeFunnelMetricsRequest, 'timezone' | 'now'>;
  mutedSignalsCount: number;
  requestId: string;
  dateRangeId: string;
  isRevenueFunnel: boolean;
  origin: AnalysisOrigin;
  funnel: Funnel | null;
  specObjects: BySlug<SpecObjectInfo>;
}): ConversionAnalysisRunAnalytics => ({
  ...eventsToSpecAnalytics(params.funnel?.events?.events, params.specObjects),
  requestId: params.requestId,
  errorClicks: params.signalParams.badClicks.includes(BadClickType.CLICK_TYPE_ERROR) ? 1 : 0,
  deadClicks: params.signalParams.badClicks.includes(BadClickType.CLICK_TYPE_DEAD) ? 1 : 0,
  rageClicks: params.signalParams.badClicks.includes(BadClickType.CLICK_TYPE_RAGE) ? 1 : 0,
  pagePerformance: params.signalParams.performanceThresholds.length > 0 ? 1 : 0,
  ajaxErrors: params.signalParams.ajaxErrors,
  customApiEvents: params.signalParams.customEvents.length,
  customDefinedEvents: params.signalParams.definedEvents.length,
  watchedElements: params.signalParams.seenEvents.length,
  mutedSignalsCount: params.mutedSignalsCount,
  segmentCriteriaHash: params.funnelRequest.computation?.segmentDefinitions?.length
    ? hashCode(JSON.stringify(params.funnelRequest.computation?.segmentDefinitions[0])).toString()
    : null,
  conversionCriteriaHash: params.funnelRequest.computation?.funnel?.events?.events
    ? hashCode(JSON.stringify(params.funnelRequest.computation?.funnel?.events?.events)).toString()
    : null,
  dateRangeId: params.dateRangeId,
  inSameSession: Boolean(
    params.funnelRequest.computation?.funnel?.events?.constraints?.inSameSession,
  ),
  isRevenueFunnel: params.isRevenueFunnel,
  origin: params.origin,
  pageViews: params.signalParams.pageViews.length,
  coreWebVitals: params.signalParams.coreWebVitals ? 1 : 0,
});

export const transformOpportunitiesRequest = (params: {
  req: GenerateOpportunitiesRequest;
  segmentId: string;
  funnelId: string;
  cannedDateRange: DateRange;
  signalParams: Omit<GenerateSignalsRequest, 'timezone' | 'now' | 'cacheOptions'>;
  requestId: string;
}): GenerateOpportunityRequestAnalytics => {
  const counts = countSignals(params.req.signals);

  return {
    requestId: params.requestId,
    segmentId: params.segmentId,
    conversionFunnelId: params.funnelId,
    segmentCriteriaHash: hashCode(
      JSON.stringify(params.req?.conversionParams?.segmentDefinition),
    ).toString(),
    conversionCriteriaHash: hashCode(
      JSON.stringify(params.req?.conversionParams?.funnelEventSequence?.events),
    ).toString(),
    conversionFunnelLength: params.req?.conversionParams?.funnelEventSequence?.events.length || 0,
    inSameSession: params.req?.conversionParams?.requireSameSession || false,
    analysisMode: params.req?.conversionParams?.analysisMode || AnalysisMode.ANALYSIS_MODE_INVALID,
    dateRangeId: params.cannedDateRange.id,
    badClicks: params.signalParams.badClicks,
    performanceThresholds: params.signalParams.performanceThresholds.map(p => int64.toNumber(p)),
    start: params.req?.conversionParams?.analysisTimeRange?.startTime || '',
    end: params.req?.conversionParams?.analysisTimeRange?.endTime || '',
    customEvents: params.signalParams.customEvents.length,
    seenEvents: params.signalParams.seenEvents.length,
    ajaxErrors: params.signalParams.ajaxErrors,
    signals: counts,
  };
};

export const transformGenerateOpportunitiesSuccess = (resp: GenerateOpportunitiesResponse) => {
  // individual opps can be cached at different times, so sort the list of opps and grab the most recent one
  const sortedMostRecentOpps = orderBy(
    resp.opportunities.slice(),
    d => Timestamp.toMillis(d.computedAt),
    'desc',
  );
  let funnelCounts = null;
  if (sortedMostRecentOpps.length > 0) {
    // the funnel count is equal to the number of unaffected users + the number of affected users
    funnelCounts = sortedMostRecentOpps[0]?.steps.map(
      d => int64.toNumber(d.unaffectedUsers) + int64.toNumber(d.affectedUsers),
    );
  }
  return {
    funnelCounts,
  };
};

export const transformOpportunitiesDisplayed = (
  opportunities: Opportunity[],
  mutedOpportunities: Opportunity[],
  requestId: string,
  origin: AnalysisOrigin,
  customSignalConfigured: boolean,
) => {
  const counts = countSignals(opportunities.map(d => d.signal).filter(isNonNull));
  return {
    requestId,
    totalOpportunities: opportunities.length,
    totalSignificantOpportunities: opportunities.filter(
      o =>
        o.impactStats?.isSignificant ||
        (o.impactStats?.confidence && o.impactStats.confidence >= 95),
    ).length,
    totalRageClickOpps: counts.rageClicks,
    totalDeadClickOpps: counts.deadClicks,
    totalErrorClickOpps: counts.errorClicks,
    totalPerformanceOpps: counts.performance,
    totalSeenEventOpps: counts.seenEvents,
    totalCustomApiEventOpps: counts.customApiEvents,
    totalCustomDefinedEventOpps: counts.customDefinedEvents,
    opportunitiesFound: opportunities.length > 0,
    mutedOpportunitiesFound: mutedOpportunities.length > 0,
    totalAjaxEventOpps: counts.ajaxErrors,
    origin,
    customSignalConfigured,
    totalCLSOpps: counts.cls,
    totalFIDOpps: counts.fid,
    totalLCPOpps: counts.lcp,
    totalINPOpps: counts.inp,
    totalTTIOpps: counts.tti,
    totalTBTOpps: counts.tbt,
    totalTTFBOpps: counts.ttfb,
  };
};

const countSignals = (signals: Signal[]): GenerateOpportunityRequestAnalytics['signals'] => {
  let totalRageClickOpps = 0;
  let totalDeadClickOpps = 0;
  let totalErrorClickOpps = 0;
  let totalPerformanceOpps = 0;
  let totalSeenEventOpps = 0;
  let totalCustomApiEventOpps = 0;
  let totalCustomDefinedEventOpps = 0;
  let totalAjaxEventOpps = 0;
  let totalPageViewOpps = 0;
  let totalLCPOpps = 0;
  let totalFIDOpps = 0;
  let totalCLSOpps = 0;
  let totalINPOpps = 0;
  let totalTTIOpps = 0;
  let totalTBTOpps = 0;
  let totalTTFBOpps = 0;
  signals.forEach(s => {
    switch (s.type) {
      case SignalType.SIGNAL_TYPE_BAD_CLICK:
        switch (s.clickType) {
          case BadClickType.CLICK_TYPE_RAGE:
            totalRageClickOpps++;
            break;
          case BadClickType.CLICK_TYPE_DEAD:
            totalDeadClickOpps++;
            break;
          case BadClickType.CLICK_TYPE_ERROR:
            totalErrorClickOpps++;
            break;
        }
        break;
      case SignalType.SIGNAL_TYPE_PERF_EVENT:
        totalPerformanceOpps++;
        break;
      case SignalType.SIGNAL_TYPE_SEEN_EVENT:
        totalSeenEventOpps++;
        break;
      case SignalType.SIGNAL_TYPE_CUSTOM_EVENT:
        totalCustomApiEventOpps++;
        break;
      case SignalType.SIGNAL_TYPE_DEFINED_EVENT:
        totalCustomDefinedEventOpps++;
        break;
      case SignalType.SIGNAL_TYPE_AJAX_ERROR:
        totalAjaxEventOpps++;
        break;
      case SignalType.SIGNAL_TYPE_PAGE_VIEW:
        totalPageViewOpps++;
        break;
      case SignalType.SIGNAL_TYPE_CORE_WEB_VITALS:
        switch (s.coreWebVital?.coreWebVital) {
          case CoreWebVitalType.CORE_WEB_VITAL_TYPE_LARGEST_CONTENTFUL_PAINT:
            totalLCPOpps++;
            break;
          case CoreWebVitalType.CORE_WEB_VITAL_TYPE_FIRST_INPUT_DELAY:
            totalFIDOpps++;
            break;
          case CoreWebVitalType.CORE_WEB_VITAL_TYPE_CUMULATIVE_LAYOUT_SHIFT:
            totalCLSOpps++;
            break;
          case CoreWebVitalType.WEB_VITAL_TYPE_INTERACTION_TO_NEXT_PAINT:
            totalINPOpps++;
            break;
          case CoreWebVitalType.WEB_VITAL_TYPE_TIME_TO_INTERACTIVE:
            totalTTIOpps++;
            break;
          case CoreWebVitalType.WEB_VITAL_TYPE_TOTAL_BLOCKING_TIME:
            totalTBTOpps++;
            break;
          case CoreWebVitalType.WEB_VITAL_TYPE_TIME_TO_FIRST_BYTE:
            totalTTFBOpps++;
            break;
        }
        break;
    }
  });

  return {
    total: signals.length,
    deadClicks: totalDeadClickOpps,
    rageClicks: totalRageClickOpps,
    errorClicks: totalErrorClickOpps,
    performance: totalPerformanceOpps,
    customApiEvents: totalCustomApiEventOpps,
    customDefinedEvents: totalCustomDefinedEventOpps,
    seenEvents: totalSeenEventOpps,
    ajaxErrors: totalAjaxEventOpps,
    pageViews: totalPageViewOpps,
    cls: totalCLSOpps,
    fid: totalFIDOpps,
    lcp: totalLCPOpps,
    inp: totalINPOpps,
    tti: totalTTIOpps,
    tbt: totalTBTOpps,
    ttfb: totalTTFBOpps,
  };
};
